import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VideoRingData {
  ring_request_id: string;
  call_id: string;
  current_level: number;
  timeout_seconds: number;
  available_users: any[];
}

interface VideoRingContextType {
  videoRingData: VideoRingData | null;
  isStarting: boolean;
  startVideoRing: (params: {
    contactId?: string;
    leadId?: string;
    dealId?: string;
  }) => Promise<void>;
  closeModal: () => void;
}

const VideoRingContext = createContext<VideoRingContextType | undefined>(undefined);

export function VideoRingProvider({ children }: { children: ReactNode }) {
  const [videoRingData, setVideoRingData] = useState<VideoRingData | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const startVideoRing = async (params: {
    contactId?: string;
    leadId?: string;
    dealId?: string;
  }) => {
    if (isStarting || videoRingData) {
      console.log('[VideoRingContext] ⚠️ Already processing, ignoring request');
      return;
    }

    setIsStarting(true);

    try {
      console.log('[VideoRingContext] 📞 Calling start-video-ring function...', params);

      const result = await supabase.functions.invoke('start-video-ring', {
        body: {
          contact_id: params.contactId,
          lead_id: params.leadId,
          deal_id: params.dealId,
        },
      });

      console.log('[VideoRingContext] 📦 Full result:', result);

      if (result.error) {
        console.error('[VideoRingContext] ❌ Error:', result.error);
        throw result.error;
      }

      if (!result.data) {
        console.error('[VideoRingContext] ❌ No data returned');
        throw new Error('No data returned from function');
      }

      console.log('[VideoRingContext] ✅ Setting videoRingData:', result.data);
      setVideoRingData(result.data);
    } catch (error) {
      console.error('[VideoRingContext] ❌ Caught error:', error);
      toast.error('Erro ao iniciar chamada de vídeo');
    } finally {
      setIsStarting(false);
    }
  };

  const closeModal = () => {
    console.log('[VideoRingContext] 🚪 Closing modal');
    setVideoRingData(null);
    setIsStarting(false);
  };

  return (
    <VideoRingContext.Provider value={{ videoRingData, isStarting, startVideoRing, closeModal }}>
      {children}
    </VideoRingContext.Provider>
  );
}

export function useVideoRing() {
  const context = useContext(VideoRingContext);
  if (context === undefined) {
    throw new Error('useVideoRing must be used within a VideoRingProvider');
  }
  return context;
}
